/**
 * Helpers de formatação portados do legado (cnpj, telefone, datas, números).
 * Referências: [application.js:856 formatNumber, 1069 formatDate, 5437 cnpj mask].
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

/**
 * Decodifica valores URI legados sem deixar um `%` literal ou escape incompleto derrubar a tela.
 * URLs de places vêm de fontes externas e podem misturar valores crus e percent-encoded; quando
 * o valor não é uma sequência válida, preservar o original é mais fiel do que omitir o dado.
 */
export function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Formata CNPJ de 14 dígitos: 00.000.000/0000-00. Legado: regex em showLeads. */
export function formatCnpj(cnpj?: string): string {
  if (!cnpj) return '';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/** Número com separador de milhar conforme locale. Legado: `formatNumber`. */
export function formatNumber(value: number, locale = 'pt-BR'): string {
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

/** Idiomas do navegador, na ordem de preferência; vazio fora do navegador. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

const numericLocales = new Map<string, string>();

/**
 * Locale das datas e horas só com números (decisão 201, 24/10/2026): o do navegador quando ele
 * tem o idioma do painel (o primeiro da lista de idiomas do navegador que coincide), senão o
 * padrão do idioma do painel. Painel em inglês num navegador en-GB mostra 24/10/2026, 15:30;
 * num navegador em português, 10/24/2026, 3:30 PM. Datas com o nome do mês continuam no locale
 * do painel, para não misturar línguas, e o fuso é sempre o do computador.
 */
export function numericDateLocale(
  panelLocale: string,
  languages: readonly string[] = browserLanguages(),
): string {
  const key = `${panelLocale}|${languages.join(',')}`;
  const cached = numericLocales.get(key);
  if (cached) return cached;
  const lang = (panelLocale.split('-')[0] ?? '').toLowerCase();
  let result = panelLocale;
  for (const tag of languages) {
    if ((tag.split('-')[0] ?? '').toLowerCase() !== lang) continue;
    try {
      const [canonical] = Intl.getCanonicalLocales(tag);
      if (canonical && Intl.DateTimeFormat.supportedLocalesOf(canonical).length > 0) {
        result = canonical;
        break;
      }
    } catch {
      // Etiqueta inválida: tenta a próxima.
    }
  }
  numericLocales.set(key, result);
  return result;
}

/** Data + hora (dd/mm/aaaa, hh:mm) conforme locale — igual ao legado (`formatDate` [1069]). */
export function formatDate(value: string | number | Date, locale = 'pt-BR'): string {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(numericDateLocale(locale), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Emoji de bandeira a partir do ISO-2. Legado: `getFlagEmoji` [793] (uk→🇬🇧). */
export function flagEmoji(iso2?: string): string {
  if (!iso2 || iso2.length !== 2) return '';
  if (iso2.toLowerCase() === 'uk') return '🇬🇧';
  const code = iso2.toUpperCase();
  const A = 0x1f1e6;
  const first = code.charCodeAt(0) - 65;
  const second = code.charCodeAt(1) - 65;
  if (first < 0 || first > 25 || second < 0 || second > 25) return '';
  return String.fromCodePoint(A + first, A + second);
}

/**
 * Rótulo de expiração para as feature-tags da home (porta de `formatExpire` [862]):
 * "112 Dias" / "5 Horas" / "Expirado" / "Vitalício" / "Assinante" (capitalizado).
 */
export function formatExpire(
  expire: Date,
  t: (key: string, fallback: string) => string,
): { text: string; expired: boolean } {
  const seconds = Math.floor((expire.getTime() - Date.now()) / 1000);
  const DAY = 60 * 60 * 24;
  if (Number.isNaN(seconds)) return { text: '', expired: false };
  if (seconds > 366 * DAY) {
    if (seconds > 365 * DAY * 10)
      return { text: capitalize(t('ltd', 'Vitalício')), expired: false };
    return { text: capitalize(t('subscribed', 'Assinante')), expired: false };
  }
  if (seconds <= 0) return { text: capitalize(t('expired', 'Expirado')), expired: true };
  const days = Math.floor(seconds / DAY);
  if (days > 1) return { text: `${days} ${capitalize(t('days', 'dias'))}`, expired: false };
  const hours = Math.floor(seconds / 3600);
  if (hours > 0)
    return {
      text: `${hours} ${capitalize(t(hours > 1 ? 'hours' : 'hour', 'horas'))}`,
      expired: false,
    };
  const minutes = Math.floor(seconds / 60);
  if (minutes > 0) {
    return {
      text: `${minutes} ${capitalize(t(minutes > 1 ? 'minutes' : 'minute', 'minutos'))}`,
      expired: false,
    };
  }
  return { text: capitalize(t('expired', 'Expirado')), expired: true };
}

/** Capitaliza a primeira letra. Legado: `capitalize` [762]. */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Máscara de telefone para exibição (ex.: "+554830372736" → "(48) 3037-2736"). O legado nunca
 * mascarou a lista de leads "phones" — `showLeads` [application.js:5427] mostra `row.phone` cru;
 * só `showPlaces` [application.js:5830] exibe formatado, porque ali o valor já chega pronto do
 * scraper (`phone.formated`, calculado uma vez na captura). A lista de leads "phones" nunca teve
 * esse campo — aqui o formato nacional é calculado no próprio render, sem alterar o valor
 * armazenado nem o `tel:` do link, que continuam usando o original.
 */
export function formatPhoneDisplay(phone?: string): string {
  if (!phone) return '';
  try {
    const parsed = parsePhoneNumberFromString(
      phone,
      phone.trim().startsWith('+') ? undefined : 'BR',
    );
    return parsed?.isValid() ? parsed.formatNational() : phone;
  } catch {
    return phone;
  }
}
